import React, { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { FundamentalType } from 'domain/models';
import {
  ActionButton,
  CrosshairIcon,
  DocumentReportAnIssue,
  DocumentViewer,
  SideBar
} from 'presentation/components';
import { useSourceServices } from 'presentation/hooks';
import { CompositeFundamental } from './CompositeFundamental';
import { Header, Wrapper } from './SourcePage.styles';
import { Mixpanel } from 'infra/mixpanel';
import { SideBarRef } from './RolledUpSideBar';
import { RestatementsTable } from './RestatementsTable';

type Props = {
  fundamentalId: string;
  innerSideBarRef?: React.RefObject<SideBarRef>;
  isStitcher: boolean;
};

export const SourceContent = ({ fundamentalId, innerSideBarRef, isStitcher }: Props) => {
  const [search] = useSearchParams();

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const isAddinView = search.get('excel_plugin') === 'true';

  const url = isStitcher
    ? `/text_fundamental?${search.toString()}`
    : `embed/source/${fundamentalId}`;

  const { data, loading } = useSourceServices().fundamentalInfo({
    variables: {
      fundamentalId
    },
    skip: !fundamentalId
  });

  const isComposite = data?.fundamentalInfo.type === FundamentalType.COMPOSITE;
  const isFiller = data?.fundamentalInfo.type === FundamentalType.FILLER_VALUE;
  const hasRestatements = !!data?.fundamentalInfo.restatedFundamentals.length;

  const {
    data: compositeData,
    loading: compositeLoading
  } = useSourceServices().compositeFundamentalInfo({
    variables: {
      fundamentalId
    },
    skip: !fundamentalId || !isComposite
  });

  const handleLocateDatapoint = () => {
    Mixpanel.track('marketplace:src:locate', data?.fundamentalInfo);
    iframeRef?.current?.contentWindow?.postMessage('locate', '*');
  };

  useEffect(() => {
    if (data?.fundamentalInfo) {
      Mixpanel.track('marketplace:src:open', data?.fundamentalInfo);
    }
  }, [data?.fundamentalInfo]);

  if (loading || compositeLoading || !data?.fundamentalInfo) {
    return <CircularProgress size={16} style={{ margin: ' 32px auto' }} />;
  }

  const { company, document, type } = data?.fundamentalInfo;

  const fundamentalAvailable =
    type !== FundamentalType.COMPOSITE && type !== FundamentalType.FILLER_VALUE;

  return (
    <>
      {!isAddinView && (
        <SideBar
          id={fundamentalId}
          company={company}
          document={document}
          type={type}
          iframeRef={iframeRef}
          innerSideBarRef={innerSideBarRef}
        >
          {hasRestatements && (
            <RestatementsTable
              referenceFundamental={data?.fundamentalInfo.referenceFundamental}
              restatedFundamentals={data?.fundamentalInfo.restatedFundamentals}
            />
          )}
        </SideBar>
      )}
      <Wrapper>
        <Header>
          {isAddinView && <DocumentReportAnIssue id={fundamentalId} />}
          {company.ticker} - {company.name}
          {!isStitcher && (
            <ActionButton bgLight onClick={handleLocateDatapoint}>
              <CrosshairIcon />
              Locate Datapoint
            </ActionButton>
          )}
        </Header>
        {isComposite && <CompositeFundamental {...compositeData?.compositeFundamentals} />}
        {isFiller && (
          <>
            <span>This document is not available yet.</span>
            {!!document && (
              <span>
                Please try again later or check the filing here:
                <a href={document.url} target="_blank" rel="noreferrer">
                  {document.documentType}
                </a>
              </span>
            )}
          </>
        )}
        {fundamentalAvailable && (
          <DocumentViewer ref={iframeRef} url={url} title="datapoint-viewer" />
        )}
      </Wrapper>
    </>
  );
};
